/**
 * Reading a role's kick-off sheet for a suggestion — the shared state machine.
 *
 * Extracted from PanelSuggestion.tsx when the FINAL round needed the same thing
 * for its own template (final interviewer + bar raiser). The Google-token dance
 * is the fiddly part and must not be reimplemented per caller:
 *
 *  - with a token in hand the fetch starts immediately;
 *  - a consented-but-tokenless mount shows 'loading' while useGmailToken's own
 *    mount effect silently re-mints, and fires as soon as the token lands;
 *  - a never-consented user gets a connect BUTTON — never an auto-popup, because
 *    a popup without a click is blocked and burns the user activation;
 *  - a failed silent mint drops to that button after 8s instead of spinning
 *    forever;
 *  - a 401/403 (expired or revoked grant) clears the token and returns to the
 *    connect prompt rather than a dead-end "lookup failed".
 *
 * The result is only ever a PRE-FILL. Nothing is persisted here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError } from '../../../../shared/utils/authenticatedFetch';
import * as api from '../../services/taActionBoardApi';
import { useGmailConnection } from './GmailConnectionContext';
import { hasConsentedBefore } from './useGmailToken';

/** How long a silent re-mint may take before we show the connect button. */
const MINT_TIMEOUT_MS = 8000;

export type SuggestState<T> =
  | { phase: 'loading' }
  | { phase: 'found'; suggestion: T; unresolved: string[]; kickoffUrl: string | null }
  | { phase: 'none'; reason: string; kickoffUrl: string | null }
  | { phase: 'connect' };

export interface KickoffSuggestion<T> {
  state: SuggestState<T>;
  /** For the connect BUTTON only — may open an OAuth popup, so needs a gesture. */
  connectAndFetch: () => Promise<void>;
  /** Puts the UI back in 'loading' while a click-driven connect runs. */
  setLoading: () => void;
  connecting: boolean;
}

/**
 * @param postingId  the Lever posting whose sheet to read
 * @param stage      which section of the sheet — 'case' or 'final'
 * @param myEmail    the signed-in user, for the "has consented before" check
 */
export function useKickoffSuggestion<T>(
  postingId: string,
  stage: 'case' | 'final',
  myEmail: string
): KickoffSuggestion<T> {
  const gmail = useGmailConnection();
  // Initial phase decided up front, NOT in an effect — see the header.
  const [state, setState] = useState<SuggestState<T>>(() =>
    gmail.token || hasConsentedBefore(myEmail) ? { phase: 'loading' } : { phase: 'connect' }
  );
  const requestedRef = useRef(false);

  const fetchSuggestion = useCallback(
    async (token: string | null) => {
      try {
        const res = await api.fetchPanelSuggestion(postingId, token, stage);
        if (res.suggestion) {
          setState({
            phase: 'found',
            suggestion: res.suggestion as T,
            unresolved: res.unresolved || [],
            kickoffUrl: res.kickoffUrl,
          });
        } else if (res.needsConnect) {
          setState({ phase: 'connect' });
        } else {
          setState({
            phase: 'none',
            reason: res.reason || res.error || 'No suggestion available.',
            kickoffUrl: res.kickoffUrl ?? null,
          });
        }
      } catch (err) {
        // authenticatedFetch THROWS on non-2xx — the proxy's expired-session 401
        // (and a stale/revoked grant) must land back on the connect prompt.
        if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
          gmail.clear();
          setState({ phase: 'connect' });
          return;
        }
        setState({ phase: 'none', reason: 'The kick-off sheet lookup failed.', kickoffUrl: null });
      }
    },
    [postingId, stage, gmail]
  );

  const connectAndFetch = useCallback(async () => {
    const token = gmail.token || (await gmail.connect());
    if (token) await fetchSuggestion(token);
    else setState({ phase: 'connect' });
  }, [gmail, fetchSuggestion]);

  // Both effects synchronize with an EXTERNAL system (Google OAuth mint + proxy
  // fetch); every setState they reach runs in an async continuation — a network
  // round-trip or a timer — never synchronously in the effect body.
  useEffect(() => {
    if (requestedRef.current || !gmail.token) return;
    requestedRef.current = true;
    void fetchSuggestion(gmail.token);
  }, [gmail.token, fetchSuggestion]);

  useEffect(() => {
    if (gmail.token || !hasConsentedBefore(myEmail)) return;
    const timer = window.setTimeout(() => {
      if (!requestedRef.current) setState({ phase: 'connect' });
    }, MINT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [gmail.token, myEmail]);

  return {
    state,
    connectAndFetch,
    setLoading: () => setState({ phase: 'loading' }),
    connecting: gmail.connecting,
  };
}
